/**
 * Norwegian postcode ranges accepted for Safe-4 devices (SEC-370).
 *
 * A Norwegian Safe-4 device must have a 4-digit postcode inside one of these
 * county ranges; the server refuses the save otherwise. Some ranges overlap at
 * the county borders, which is fine: we only check that the postcode is in
 * SOME range.
 *
 * Keep in sync with the server copy: securecloud-server/integration/safe4/postcodes.js
 */
export const NO_POSTCODE_RANGES = [
  { county: "Oslo", ranges: [[1, 1483]] },
  { county: "Akershus", ranges: [[1300, 1556], [1900, 2170]] },
  { county: "Østfold", ranges: [[1501, 1950]] },
  { county: "Hedmark", ranges: [[2201, 2616]] },
  { county: "Oppland", ranges: [[2601, 2985]] },
  { county: "Buskerud", ranges: [[3001, 3058], [3300, 3648]] },
  { county: "Vestfold", ranges: [[3060, 3297]] },
  { county: "Telemark", ranges: [[3650, 3999]] },
  { county: "Rogaland", ranges: [[4001, 4600], [5501, 5595]] },
  { county: "Vest-Agder", ranges: [[4400, 4720]] },
  { county: "Aust-Agder", ranges: [[4724, 4994]] },
  { county: "Hordaland", ranges: [[5003, 5994]] },
  { county: "Møre og Romsdal", ranges: [[6001, 6699]] },
  { county: "Sogn og Fjordane", ranges: [[6700, 6996]] },
  { county: "Sør-Trøndelag", ranges: [[7003, 7596]] },
  { county: "Nord-Trøndelag", ranges: [[7500, 7533], [7600, 7994]] },
  // 8986 is Vega (street addresses), just past the PM's 8985 (SEC-373, Posten's register)
  { county: "Nordland", ranges: [[8001, 8986]] },
  { county: "Troms", ranges: [[9006, 9498]] },
  { county: "Finnmark", ranges: [[9501, 9991]] },
];

/**
 * Split a one-line address ("Storgata 1, 0593 Oslo" / "Gatan 2 123 45 Stad")
 * into { street, zip, city } for the Safe-4 fields. A line without a
 * recognizable postcode goes into street as a whole, so nothing is lost and
 * the user only has to complete it.
 */
export function splitAddressLine(line) {
  const text = String(line || "").trim();
  const match = text.match(/^(.*?)[,\s]+(\d{4}|\d{3}\s?\d{2})\s+(.+)$/);
  if (match) return { street: match[1].trim(), zip: match[2].replace(/\s+/g, ""), city: match[3].trim() };
  return { street: text, zip: "", city: "" };
}

/**
 * True when the zip is a Swedish postcode: five digits, the first not 0
 * ("123 45" and "SE-123 45" are fine). A format check only - Sweden has no
 * range list like Norway (SEC-373).
 */
export function isValidSePostcode(zip) {
  const digits = String(zip || "").toUpperCase().replace(/^SE-?/, "").replace(/\s+/g, "");
  return /^[1-9]\d{4}$/.test(digits);
}

/** True when the zip is a 4-digit Norwegian postcode inside one of the ranges. */
export function isValidNoPostcode(zip) {
  const digits = String(zip || "").replace(/\s+/g, "");
  if (!/^\d{4}$/.test(digits)) return false;
  const n = Number(digits);
  return NO_POSTCODE_RANGES.some(({ ranges }) => ranges.some(([from, to]) => n >= from && n <= to));
}
